import React, { useContext, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useSelector } from "react-redux";
import CartProduct from "./CartProduct";
import {
  DEFAULT_CURRENCY,
  freeShippingThreshold,
  getShippingCostPrice,
  qualifiesForFreeShipping,
} from "../../../utils/global_const";
import { ProductContext } from "../../../context/ProductContext";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../utils/navigation";
import { useTranslation } from "react-i18next";

/* Minimal / editorial cart panel - flat, sharp corners, hairline dividers,
   understated controls. No boxed cards, no drop shadows on items. */

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;
const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;
const slideInD = keyframes`
  from { transform: translateY(12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;
const slideOutD = keyframes`
  from { transform: translateY(0); opacity: 1; }
  to { transform: translateY(12px); opacity: 0; }
`;

const ContainerX = styled.div`
  position: relative;
`;
const Container = styled.div`
  margin-top: var(--spacing-xxs);
  background-color: transparent;
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--zindex-default);
  min-width: 100%;
  height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 400ms ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
`;
const Wrapper = styled.div`
  z-index: calc(var(--zindex-default) + 1);
  background-color: var(--bg-100);
  border-left: 1px solid var(--bg-300);
  width: 100%;
  height: 100%;
  min-height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${slideIn} 0.4s ease-out forwards;
        `
      : css`
          animation: ${slideOut} 0.1s ease-in forwards;
        `}
  @media (min-width: 768px) {
    position: absolute;
    right: 0%;
    top: calc(100% + 10px);
    width: 380px;
    height: auto;
    max-height: 80vh;
    min-height: auto;
    border: 1px solid var(--bg-300);
    box-shadow: var(--shadow-medium);
    ${({ $isOpen }) =>
      $isOpen
        ? css`
            animation: ${slideInD} 0.25s ease-out forwards;
          `
        : css`
            animation: ${slideOutD} 0.1s ease-in forwards;
          `};
  }
`;

const CartContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;
  cursor: pointer;
  @media (min-width: 768px) {
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  }
  svg {
    fill: var(--text-100);
    transition: fill 0.4s ease;
  }
  &:hover svg {
    fill: var(--text-200);
  }
`;
const SumQuantity = styled.div`
  cursor: pointer;
  position: absolute;
  right: -8px;
  top: -4px;
  background-color: var(--text-100);
  color: var(--bg-100);
  border-radius: 0;
  padding: 2px 4px;
  margin: 0;
  min-width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 400;
`;

const XDiv = styled.div`
  padding: var(--spacing-lg) var(--spacing-lg) var(--spacing-md);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  background-color: var(--bg-100);
  border-bottom: 2px solid var(--text-100);
`;
const ShippingBar = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--primary-300);
`;
const ShippingText = styled.p`
  margin: 0 0 6px 0;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--primary-100);
  display: flex;
  align-items: center;
  gap: 6px;
`;
const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: var(--border-radius-large);
  background-color: var(--bg-100);
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-100);
  width: ${(props) => props.$percent}%;
  transition: width 0.4s ease;
`;
const HeaderMain = styled.h3`
  margin: 0;
  padding: 0;
  font-size: var(--font-size-base);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-100);
`;
const HeaderCount = styled.span`
  font-weight: 400;
  color: var(--text-200);
`;
const CloseText = styled.span`
  cursor: pointer;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-200);
  border-bottom: 1px solid var(--text-200);
  &:hover {
    color: var(--text-100);
    border-bottom-color: var(--text-100);
  }
`;

const MiddleDiv = styled.div`
  flex: 1;
  padding: 0 var(--spacing-lg);
  max-height: calc(100vh - var(--navbar-height) * 3 - 30px);
  @media (min-width: 768px) {
    max-height: 400px;
  }
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--bg-300);
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xxl) 0;
`;
const EmptyTitle = styled.h4`
  margin: 0;
  font-weight: 400;
  color: var(--text-100);
`;
const EmptySubtitle = styled.p`
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;
const EmptyLink = styled.span`
  margin-top: var(--spacing-xs);
  cursor: pointer;
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-100);
  border-bottom: 1px solid var(--text-100);
`;

const NoticeCallout = styled.div`
  border-left: 2px solid var(--warning-color);
  padding: var(--spacing-xs) var(--spacing-sm);
  margin: var(--spacing-sm) 0;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const Bottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: var(--spacing-lg);
  border-top: 2px solid var(--text-100);
  background-color: var(--bg-100);
  box-sizing: border-box;
  @media (min-width: 768px) {
    position: static;
  }
`;
const FlexDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;
const SavingsText = styled.p`
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 11px;
  color: var(--text-200);
`;
const VatRow = styled(FlexDiv)`
  font-size: var(--font-size-small);
  color: var(--text-200);
  margin-top: 4px;
`;
const SubtotalLabel = styled.span`
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-200);
`;
const SubtotalValue = styled.span`
  font-size: var(--font-size-xlarge);
  font-weight: 400;
  color: var(--text-100);
`;
const CheckoutButton = styled.button`
  margin-top: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-sm) 0;
  background-color: var(--text-100);
  color: var(--bg-100);
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 400;
  &:hover {
    background-color: var(--primary-100);
  }
  &:disabled {
    background-color: var(--bg-300);
    color: var(--text-200);
    cursor: not-allowed;
  }
`;

const CartMenu = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldBeVisible, setShouldBeVisible] = useState(false);
  const { currencyTag, shippingMethod } = useContext(ProductContext);
  const navigate = useNavigate();
  const { goToCheckout, goToShop } = useNavigation();

  useEffect(() => {
    if (isOpen) {
      setShouldBeVisible(true);
    } else {
      const timer = setTimeout(() => {
        setShouldBeVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const cartItems = useSelector((state) => state.cart.itemsList);
  let totalQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const subtotal = cartItems.reduce(
    (total, item) => total + item.product.discount_price * item.quantity,
    0
  );
  const subtotalRounded = parseFloat(subtotal.toFixed(2));
  const savings = cartItems.reduce(
    (totalSavings, item) =>
      totalSavings +
      (item.product.price - item.product.discount_price) * item.quantity,
    0
  );
  const savingsRounded = parseFloat(savings.toFixed(2));
  const [shippingCost, setShippingCost] = useState(0.0);
  const currency = localStorage.getItem("currency") || DEFAULT_CURRENCY;
  useEffect(() => {
    if (qualifiesForFreeShipping(subtotalRounded, shippingMethod.name)) {
      setShippingCost(0);
    } else {
      setShippingCost(
        getShippingCostPrice(shippingMethod.name, currency, DEFAULT_CURRENCY)
      );
    }
  }, [subtotalRounded, shippingMethod]);

  const remainingForFreeShipping = parseFloat(
    Math.max(freeShippingThreshold - subtotalRounded, 0).toFixed(2)
  );
  const freeShippingUnlocked = remainingForFreeShipping === 0;
  const shippingProgressPercent = Math.min(
    (subtotalRounded / freeShippingThreshold) * 100,
    100
  );

  const grandTotal = subtotalRounded + shippingCost;
  const grandTotalRounded = parseFloat(grandTotal.toFixed(2));

  const inStockItems = cartItems.filter(
    (item) => item.product.is_in_stock === "in_stock"
  );
  const otherItems = cartItems.filter(
    (item) => item.product.is_in_stock !== "in_stock"
  );

  const goShopping = () => {
    setIsOpen(false);
    navigate(goToShop());
  };

  return (
    <ContainerX>
      <CartContainer onClick={toggleMenu}>
        <svg
          fill="var(--text-100)"
          width="20px"
          height="20px"
          viewBox="0 0 32 32"
          id="Outlined"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title />
          <g id="Fill">
            <path d="M23.78,11,22.35,5.27A3,3,0,0,0,19.44,3H12.56A3,3,0,0,0,9.65,5.27L8.22,11H2.16L6.09,26.73A3,3,0,0,0,9,29H23a3,3,0,0,0,2.91-2.27L29.84,11ZM15,21v6H12.83L11.7,21Zm-3.67-2L10.2,13H15v6ZM17,21h3.3l-1.13,6H17Zm0-2V13h4.8l-1.13,6Zm6.83-6h3.45l-1.5,6H22.7ZM11.59,5.76a1,1,0,0,1,1-.76h6.88a1,1,0,0,1,1,.76L21.72,11H10.28ZM8.17,13,9.3,19H6.22l-1.5-6ZM8,26.24,6.72,21h3l1.12,6H9A1,1,0,0,1,8,26.24Zm15.94,0A1,1,0,0,1,23,27H21.21l1.12-6h3Z" />
          </g>
        </svg>
        {totalQuantity !== 0 && <SumQuantity>{totalQuantity}</SumQuantity>}
      </CartContainer>

      <Container
        onClick={toggleMenu}
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
      />
      <Wrapper
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <XDiv>
          <div>
            <HeaderMain>
              {t("CART.TITLE")} <HeaderCount>({totalQuantity})</HeaderCount>
            </HeaderMain>
          </div>
          <CloseText
            onClick={toggleMenu}
            role="button"
            tabIndex={0}
            aria-label={t("ARIA.CLOSE_CART")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleMenu();
              }
            }}
          >
            {t("ARIA.CLOSE")}
          </CloseText>
        </XDiv>
        {totalQuantity > 0 && (
          <ShippingBar>
            <ShippingText>
              {freeShippingUnlocked
                ? `🎉 ${t("CART.FREE_SHIPPING_UNLOCKED")}`
                : t("CART.FREE_SHIPPING_PROGRESS", {
                    amount: `${currencyTag}${remainingForFreeShipping.toFixed(2)}`,
                  })}
            </ShippingText>
            <ProgressTrack>
              <ProgressFill $percent={shippingProgressPercent} />
            </ProgressTrack>
          </ShippingBar>
        )}
        <MiddleDiv>
          {cartItems.length === 0 ? (
            <EmptyContainer>
              <EmptyTitle>{t("CART.EMPTY_TITLE")}</EmptyTitle>
              <EmptySubtitle>{t("CART.EMPTY_SUBTITLE")}</EmptySubtitle>
              <EmptyLink
                onClick={goShopping}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") goShopping();
                }}
              >
                {t("BUTTONS.SHOP_NOW")}
              </EmptyLink>
            </EmptyContainer>
          ) : (
            <>
              {inStockItems.map((item, index) => (
                <CartProduct item={item} key={index} />
              ))}
              {otherItems.length > 0 && (
                <>
                  <NoticeCallout>{t("CART.NOTICE")}</NoticeCallout>
                  {otherItems.map((item, idx) => (
                    <CartProduct item={item} key={`out-${idx}`} />
                  ))}
                </>
              )}
            </>
          )}
        </MiddleDiv>
        {cartItems.length > 0 && (
          <Bottom>
            {savingsRounded > 0 && (
              <SavingsText>
                {t("CART.SAVINGS")} {currencyTag}
                {savingsRounded}
              </SavingsText>
            )}
            <FlexDiv>
              <SubtotalLabel>{t("CART.SHOPPING_BASKET")}</SubtotalLabel>
              <SubtotalValue>
                {currencyTag}
                {grandTotalRounded}
              </SubtotalValue>
            </FlexDiv>
            <VatRow>
              <span>{t("CART.VAT")}</span>
              <span>{t("CART.INCLUDED")}</span>
            </VatRow>
            <CheckoutButton
              onClick={() => navigate(goToCheckout())}
              disabled={totalQuantity === 0}
            >
              {t("CART.CHECKOUT")}
            </CheckoutButton>
          </Bottom>
        )}
      </Wrapper>
    </ContainerX>
  );
};

export default CartMenu;
